import type { ActivityDefinition, Extension, ObservationDefinition, Resource } from "@medplum/fhirtypes";
import { CATALOG_MODEL_KIND_SYSTEM, catalogNameToFhir, DIAGNOSTIC_DEFINITION_KIND } from "@repo/api";
import {
  type Availability,
  DEFAULT_STANDARD_OBSERVATIONS,
  type StandardObservationKey,
  type StandardParameterReference,
} from "@repo/domain";
import { codeSystemUrl, extensionUrl } from "@repo/fhir-systems";
import { CATALOG_VALUE_URL, type CatalogValueDto, decodeCatalogValue, encodeCatalogValue } from "./catalog-value";
import {
  isStandardParameterExtension,
  parameterExtensions,
  readStandardParameterReference,
} from "./standard-parameter-reference";

/**
 * Where an `ActivityDefinition` row keeps the diagnostic facts the views read
 * (ADR-0021: views read FHIR rows directly and never import codecs).
 *
 * ONE RESOURCE TYPE, TWO MODELS. `ActivityDefinition` stores both a diagnostic
 * and a treatment definition, so the kind tag is what says which; the queries
 * already filter on the `catalogModelKind` column derived from it, and these
 * readers assume a row that filter let through.
 *
 * Revisions are instances rather than FHIR history, so the lineage rides on
 * `url`, the revision on `version`, and `date` carries the MINT date that is
 * never rewritten by a correction. Observations persist as contained
 * `ObservationDefinition`s reached through `observationResultRequirement`,
 * whose contained ids are the member ids.
 *
 * ROW FACTS THROW, MEMBERS DROP. A row missing its url, revision, mint date,
 * name, availability or default duration is unreadable and says so: a
 * substituted value is indistinguishable from a real one, so every broken row
 * would share a `""` lineage and an unknown status would hide an active
 * diagnostic from the active list. A single member the row cannot express is
 * dropped instead — see the projection, which runs inside a live query where a
 * throw blanks the page rather than costing one row.
 */

const DIAGNOSTIC_OBSERVATION_DEFINITION_KIND = "diagnostic-observation-definition";
const DIAGNOSTIC_OBSERVATION_CODE_SYSTEM = codeSystemUrl("diagnostic-observation");

export const DIAGNOSTIC_REPEATABLE_URL = extensionUrl("diagnostic-observation-repeatable");
export const DIAGNOSTIC_OBSERVATION_NOTE_LABEL_URL = extensionUrl("diagnostic-observation-note-label");
export const DIAGNOSTIC_COMPONENT_URL = extensionUrl("diagnostic-component");
export const DIAGNOSTIC_PARAMETER_URL = extensionUrl("diagnostic-parameter");
export const DIAGNOSTIC_DEVICE_REQUIREMENT_URL = extensionUrl("diagnostic-device-requirement");
export const DIAGNOSTIC_LOCATION_REQUIREMENT_URL = extensionUrl("diagnostic-location-requirement");
export const DIAGNOSTIC_PROCEDURE_NOTE_LABEL_URL = extensionUrl("diagnostic-procedure-note-label");
export const DIAGNOSTIC_STANDARD_OBSERVATION_OPT_OUT_URL = extensionUrl(
  "diagnostic-definition-standard-observation-opt-out",
);
/** The server's own member-id high-water record. Read by the codec, by nothing here. */
export const CATALOG_MEMBER_MARKS_URL = extensionUrl("catalog-member-marks");

/** The two statuses the writer emits, and the only two that read back. */
const AVAILABILITY_BY_STATUS: Readonly<Record<string, Availability>> = { active: "Active", retired: "Inactive" };

function unreadable(resource: ActivityDefinition, field: string, value: unknown): never {
  throw new Error(
    `Diagnostic definition row ${resource.id ?? "<unknown>"} has no readable ${field}${
      value === undefined ? "" : ` (${JSON.stringify(value)})`
    }`,
  );
}

function extensionAt(extensions: Extension[] | undefined, url: string): Extension | undefined {
  return extensions?.find((candidate) => candidate.url === url);
}

function extensionsAt(extensions: Extension[] | undefined, url: string): Extension[] {
  return (extensions ?? []).filter((candidate) => candidate.url === url);
}

function hasKind(resource: Resource, kind: string): boolean {
  return (resource.meta?.tag ?? []).some((tag) => tag.system === CATALOG_MODEL_KIND_SYSTEM && tag.code === kind);
}

// ---------- row facts ----------

/** The lineage canonical, shared by every revision. */
export function diagnosticDefinitionUrl(resource: ActivityDefinition): string {
  return resource.url ?? unreadable(resource, "lineage url", undefined);
}

export function diagnosticDefinitionVersion(resource: ActivityDefinition): number {
  const version = Number(resource.version);
  return Number.isFinite(version) ? version : unreadable(resource, "revision", resource.version);
}

/** The MINT date, stamped once, which as-of reconstruction resolves against. */
export function diagnosticDefinitionMintedAt(resource: ActivityDefinition): string {
  return resource.date ?? unreadable(resource, "mintedAt", undefined);
}

export function diagnosticDefinitionName(resource: ActivityDefinition): string {
  return resource.title ?? unreadable(resource, "name", undefined);
}

export function diagnosticDefinitionDescription(resource: ActivityDefinition): string | undefined {
  return resource.description;
}

export function diagnosticDefinitionAvailability(resource: ActivityDefinition): Availability {
  return AVAILABILITY_BY_STATUS[resource.status ?? ""] ?? unreadable(resource, "availability", resource.status);
}

export function diagnosticProcedureNoteLabel(resource: ActivityDefinition): string | undefined {
  return extensionAt(resource.extension, DIAGNOSTIC_PROCEDURE_NOTE_LABEL_URL)?.valueString;
}

export function diagnosticLocationRequirement(resource: ActivityDefinition): string | undefined {
  return extensionAt(resource.extension, DIAGNOSTIC_LOCATION_REQUIREMENT_URL)?.valueCode;
}

/**
 * Stored as OPT-OUTS, so the enabled set is the platform's defaults minus the
 * ones this revision turned off. An unknown opt-out code simply matches no
 * default and leaves the set alone.
 */
export function diagnosticStandardObservations(resource: ActivityDefinition): readonly StandardObservationKey[] {
  const optedOut = new Set(
    extensionsAt(resource.extension, DIAGNOSTIC_STANDARD_OBSERVATION_OPT_OUT_URL).map(
      (candidate) => candidate.valueCode,
    ),
  );
  return DEFAULT_STANDARD_OBSERVATIONS.filter((key) => !optedOut.has(key));
}

// ---------- members ----------

/**
 * The contained observation panels, in the order the requirement list names
 * them. That order is authored content; `contained` is a bag.
 */
export function diagnosticObservationDefinitions(resource: ActivityDefinition): ObservationDefinition[] {
  const contained = new Map(
    (resource.contained ?? [])
      .filter(
        (candidate): candidate is ObservationDefinition =>
          candidate.resourceType === "ObservationDefinition" &&
          hasKind(candidate, DIAGNOSTIC_OBSERVATION_DEFINITION_KIND),
      )
      .map((candidate) => [candidate.id, candidate] as const),
  );
  return (resource.observationResultRequirement ?? []).flatMap((requirement) => {
    // A contained reference, so only the `#id` form names one of these panels.
    const reference = requirement.reference ?? "";
    const found = reference.startsWith("#") ? contained.get(reference.slice(1)) : undefined;
    return found ? [found] : [];
  });
}

export function observationLabel(definition: ObservationDefinition): string | undefined {
  const panel = definition.code.coding?.find((coding) => coding.system === DIAGNOSTIC_OBSERVATION_CODE_SYSTEM);
  return definition.preferredReportName ?? panel?.display;
}

export function observationRepeatable(definition: ObservationDefinition): boolean | undefined {
  return extensionAt(definition.extension, DIAGNOSTIC_REPEATABLE_URL)?.valueBoolean;
}

export function observationNoteLabel(definition: ObservationDefinition): string | undefined {
  return extensionAt(definition.extension, DIAGNOSTIC_OBSERVATION_NOTE_LABEL_URL)?.valueString;
}

export function observationComponents(definition: ObservationDefinition): Extension[] {
  return extensionsAt(definition.extension, DIAGNOSTIC_COMPONENT_URL);
}

export function diagnosticParameters(resource: ActivityDefinition): Extension[] {
  return extensionsAt(resource.extension, DIAGNOSTIC_PARAMETER_URL);
}

export function diagnosticDeviceRequirements(resource: ActivityDefinition): Extension[] {
  return extensionsAt(resource.extension, DIAGNOSTIC_DEVICE_REQUIREMENT_URL);
}

/** Every member keeps its id, label and value under these fixed child urls. */
export function memberField(member: Extension, url: string): Extension | undefined {
  return member.extension?.find((candidate) => candidate.url === url);
}

/**
 * Read every field the diagnostic projection will later need, and discard it.
 *
 * The projection runs inside a live query's `fn.select`, where a throw blanks
 * the whole page instead of costing one row. Reading it all here instead means
 * `deriveForSync` in `./base.ts` catches an unreadable row, logs its id and
 * drops just that row before the projection ever meets it.
 *
 * The member readers below do not throw on their own — the projection drops a
 * member it cannot express — but they are still exercised here, because
 * walking them is what surfaces a contained panel with no `code` at all, which
 * throws a `TypeError` from inside `fn.select` and takes the page with it.
 */
export function validateDiagnosticDefinitionRow(resource: ActivityDefinition): void {
  diagnosticDefinitionUrl(resource);
  diagnosticDefinitionVersion(resource);
  diagnosticDefinitionMintedAt(resource);
  diagnosticDefinitionName(resource);
  diagnosticDefinitionDescription(resource);
  diagnosticDefinitionAvailability(resource);
  diagnosticProcedureNoteLabel(resource);
  diagnosticLocationRequirement(resource);
  diagnosticStandardObservations(resource);
  for (const definition of diagnosticObservationDefinitions(resource)) {
    observationLabel(definition);
    observationRepeatable(definition);
    observationNoteLabel(definition);
    for (const component of observationComponents(definition)) {
      memberField(component, "id");
      memberField(component, "label");
      decodeCatalogValue(memberField(component, CATALOG_VALUE_URL));
    }
  }
  for (const parameter of diagnosticParameters(resource)) {
    if (isStandardParameterExtension(parameter)) {
      readStandardParameterReference(parameter);
      continue;
    }
    memberField(parameter, "id");
    memberField(parameter, "label");
    decodeCatalogValue(memberField(parameter, CATALOG_VALUE_URL));
  }
  for (const requirement of diagnosticDeviceRequirements(resource)) {
    memberField(requirement, "id");
    memberField(requirement, "definition");
  }
}

// ---------- the optimistic writer ----------

/**
 * The inverse of the readers above, for optimistic rows only.
 *
 * An optimistic row is read back through the SAME projection a synced row
 * takes, so it has to be written in the same storage shape — any difference
 * reads as a flicker when the server row lands. This is the read side's twin of
 * `diagnostic-definition.codec.ts`'s encode, and the storage test round-trips a
 * capture of that encoder through both directions to prove the two agree.
 *
 * The member MARKS are deliberately absent: they are the server's own member-id
 * high-water record, the client has no honest value for them, and nothing on
 * this side reads them. The synced row brings them.
 */

export interface DiagnosticDefinitionRowSpine {
  url: string;
  version: number;
  mintedAt: string;
  name: string;
  description?: string;
  availability: Availability;
}

export interface DiagnosticDefinitionRowComponent {
  id: string;
  label: string;
  value: CatalogValueDto;
}

export interface DiagnosticDefinitionRowObservation {
  id: string;
  label: string;
  repeatable: boolean;
  noteLabel?: string;
  components: readonly DiagnosticDefinitionRowComponent[];
}

export interface DiagnosticDefinitionRowParameter {
  id: string;
  label: string;
  value: CatalogValueDto;
}

export interface DiagnosticDefinitionRowContent {
  procedureNoteLabel?: string;
  standardObservations: readonly StandardObservationKey[];
  observations: readonly DiagnosticDefinitionRowObservation[];
  parameters: readonly (DiagnosticDefinitionRowParameter | StandardParameterReference)[];
  deviceRequirements: readonly { id: string; definition: string }[];
  locationRequirement?: string;
}

/**
 * A member with no identity is a defect upstream, not a row to paper over with
 * a blank id: the id is what a later revision edits the member by, and a `""`
 * one would collide with every other member that lost its own.
 */
function writtenMemberId(id: string, collection: string): string {
  if (!id) throw new Error(`A diagnostic definition ${collection} reached persistence with no member identity`);
  return id;
}

function componentToRow(component: DiagnosticDefinitionRowComponent): Extension {
  return {
    url: DIAGNOSTIC_COMPONENT_URL,
    extension: [
      { url: "id", valueString: writtenMemberId(component.id, "component") },
      { url: "label", valueString: component.label },
      encodeCatalogValue(component.value),
    ],
  };
}

function observationToRow(observation: DiagnosticDefinitionRowObservation): ObservationDefinition {
  const id = writtenMemberId(observation.id, "observation");
  return {
    resourceType: "ObservationDefinition",
    id,
    meta: { tag: [{ system: CATALOG_MODEL_KIND_SYSTEM, code: DIAGNOSTIC_OBSERVATION_DEFINITION_KIND }] },
    // FHIR requires this slot; the member id fills it without vocabulary meaning.
    code: { coding: [{ system: DIAGNOSTIC_OBSERVATION_CODE_SYSTEM, code: id, display: observation.label }] },
    preferredReportName: observation.label,
    multipleResultsAllowed: observation.repeatable,
    extension: [
      { url: DIAGNOSTIC_REPEATABLE_URL, valueBoolean: observation.repeatable },
      ...(observation.noteLabel === undefined
        ? []
        : [{ url: DIAGNOSTIC_OBSERVATION_NOTE_LABEL_URL, valueString: observation.noteLabel }]),
      ...observation.components.map(componentToRow),
    ],
  };
}

function parameterToRow(parameter: DiagnosticDefinitionRowParameter): Extension {
  return {
    url: DIAGNOSTIC_PARAMETER_URL,
    extension: [
      { url: "id", valueString: writtenMemberId(parameter.id, "parameter") },
      { url: "label", valueString: parameter.label },
      encodeCatalogValue(parameter.value),
    ],
  };
}

function deviceRequirementToRow(requirement: { id: string; definition: string }): Extension {
  return {
    url: DIAGNOSTIC_DEVICE_REQUIREMENT_URL,
    extension: [
      { url: "id", valueString: writtenMemberId(requirement.id, "device requirement") },
      // Unpinned on purpose: the requirement names a DeviceDefinition LINEAGE.
      { url: "definition", valueCanonical: requirement.definition },
    ],
  };
}

export function writeDiagnosticDefinitionRow(
  spine: DiagnosticDefinitionRowSpine,
  content: DiagnosticDefinitionRowContent,
): ActivityDefinition {
  const observations = content.observations.map(observationToRow);
  return {
    resourceType: "ActivityDefinition",
    meta: { tag: [{ system: CATALOG_MODEL_KIND_SYSTEM, code: DIAGNOSTIC_DEFINITION_KIND }] },
    url: spine.url,
    version: String(spine.version),
    // The MINT date, stamped once. A correction moves `meta.lastUpdated`, so
    // resolving as-of against that would make an old revision look minted on
    // the day someone fixed a typo.
    date: spine.mintedAt,
    ...catalogNameToFhir(spine.name),
    ...(spine.description === undefined ? {} : { description: spine.description }),
    status: spine.availability === "Active" ? "active" : "retired",
    kind: "ServiceRequest",
    intent: "order",
    contained: observations,
    observationResultRequirement: observations.map((observation) => ({ reference: `#${observation.id}` })),
    extension: [
      // Stored as OPT-OUTS, the same way they read back: the enabled set is the
      // platform's defaults minus what this revision turned off.
      ...DEFAULT_STANDARD_OBSERVATIONS.filter((key) => !content.standardObservations.includes(key)).map((key) => ({
        url: DIAGNOSTIC_STANDARD_OBSERVATION_OPT_OUT_URL,
        valueCode: key,
      })),
      ...parameterExtensions(DIAGNOSTIC_PARAMETER_URL, content.parameters, parameterToRow),
      ...content.deviceRequirements.map(deviceRequirementToRow),
      ...(content.locationRequirement === undefined
        ? []
        : [{ url: DIAGNOSTIC_LOCATION_REQUIREMENT_URL, valueCode: content.locationRequirement }]),
      ...(content.procedureNoteLabel === undefined
        ? []
        : [{ url: DIAGNOSTIC_PROCEDURE_NOTE_LABEL_URL, valueString: content.procedureNoteLabel }]),
    ],
  };
}
